import { ShopOrderView } from "src/api/generated/erp/db/types/tables/shopOrderView.ts";
import {
	getShopOrderStateLabel,
	getShopOrderStateLabels,
	ShopOrderState,
	ShopOrderStateValues,
} from "src/api/generated/erp/db/types/enums/shopOrderState.ts";
import {
	dateColumn,
	dateTimeColumn,
	enumColumn,
	floatColumn,
	integerColumn,
	moneyColumn,
	textColumn,
} from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import { getShopOrderSourceTypeLabels } from "src/api/generated/erp/db/types/enums/shopOrderSourceType.ts";
import { DocumentsOfObjectButton } from "src/components/views/documents/objectDocuments/DocumentsOfObjectButton.tsx";
import { nullableAavoObjectRef } from "src/utils/aavoObjectRefUtils.ts";
import { AavoTextField } from "src/components/common/inputFields/AavoTextField.tsx";
import {
	faBan,
	faCheck,
	faCog,
	faFileDownload,
	faHourglassHalf,
	faPlusHexagon,
	faPrint,
	faShare,
	faStickyNote,
	faStopwatch20,
	faTh,
	faUndo,
} from "@fortawesome/pro-regular-svg-icons";
import { AsyncMenuButton } from "src/components/common/contextMenu/AsyncMenuButton.tsx";
import { useConfirmDialog } from "src/components/common/dialogs/confirmDialog/ConfirmDialogContext.ts";
import { useInputDialog } from "src/components/common/dialogs/input/useInputDialog.tsx";
import { ShopOrderActionApi } from "src/api/generated/erp/production/api/shopOrder/shopOrderActionApi.ts";
import { AavoSelectField } from "../../../../common/inputFields/AavoSelectField.tsx";
import { ShopOrderDataGridApi } from "src/api/generated/erp/production/api/shopOrder/shopOrderDataGridApi.ts";
import { ShopOrderPrintApi } from "src/api/generated/erp/production/api/shopOrder/shopOrderPrintApi.ts";
import { ShopOrderOperationApi } from "src/api/generated/erp/production/api/shopOrderOperation/shopOrderOperationApi.ts";
import { ShopOrderStateView } from "src/components/views/erp/production/shopOrder/ShopOrderStateView.tsx";
import { faCubes } from "@fortawesome/pro-solid-svg-icons";
import { OpenPartConfigurationButton } from "src/components/views/erp/configurator/inspecting/OpenPartConfigurationButton.tsx";
import { OpenConfigurationHistoryButton } from "src/components/views/erp/configurator/objectConfigurationHistory/OpenConfigurationHistoryButton.tsx";
import { ShopOrderConfiguratorView } from "src/components/views/erp/configurator/configuratorForm/impl/ShopOrderConfiguratorView.tsx";
import { OpenCustomerOrderInfoButton } from "src/components/views/erp/utilComponents/OpenCustomerOrderInfoButton.tsx";
import { ShopOrderForm } from "src/components/views/erp/production/shopOrder/ShopOrderForm.tsx";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { releaseShopOrder } from "src/components/views/erp/production/shopOrder/utils/releaseShopOrder.ts";
import { closeShopOrder } from "src/components/views/erp/production/shopOrder/utils/closeShopOrder.ts";
import { cancelShopOrders } from "src/components/views/erp/production/shopOrder/utils/cancelShopOrder.ts";
import { useServerSideDataGridModel } from "src/components/common/dataGrid/gridModel/useServerSideDataGridModel.tsx";
import { genericNullableValue } from "src/utils/genericNullableValue.ts";
import { MenuCheckbox } from "src/components/common/contextMenu/MenuCheckbox.tsx";
import { ControlledAsyncFormCrudDataGrid } from "src/components/common/dataGrid/crud/ControlledAsyncFormCrudDataGrid.tsx";
import { downloadFile } from "src/utils/fileDownloading.ts";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { openFormOnDialog } from "src/components/common/dialogs/formDialog/openFormOnDialog.ts";
import { IncreaseShopOrderCompletedQuantityForm } from "src/components/views/erp/production/jobQueue/base/IncreaseShopOrderCompletedQuantityForm.tsx";
import { ShopOrderBatchesView } from "src/components/views/erp/production/shopOrderBatch/ShopOrderBatchesView.tsx";
import { SurveySubmissionsOfObjectsView } from "src/components/views/erp/surveys/objectSurveySubmissions/SurveySubmissionsOfObjectsView.tsx";
import { useTenantCustomizations } from "src/tenantCustomizations/TenantCustomizationsContext.ts";
import { OpenObjectChangeLogButton } from "src/components/views/changeLogging/OpenObjectChangeLogButton.tsx";
import { ShopOrderCostEventsDataGrid } from "src/components/views/erp/production/shopOrder/ShopOrderCostEventsDataGrid.tsx";
import { Divider } from "@mui/material";
import { OpenSitePartButton } from "src/components/views/erp/utilComponents/OpenSitePartButton.tsx";
import { getPartRevisionShortDescription } from "src/components/views/erp/erpUtils.ts";
import { Permissions } from "src/api/generated/iam/permissions.ts";
import { useHasPermission } from "src/components/views/erp/common/userPermissions.ts";
import { ShopOrderSource } from "src/api/generated/erp/production/shopOrderSource.ts";
import { OpenShopOrderSourceButton } from "src/components/views/erp/production/shopOrder/OpenShopOrderSourceButton.tsx";
import { CellWithRemarkIndicator } from "src/components/common/dataGrid/cells/CellWithRemarkIndicator.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import { CustomUiActionObjectMenuItems } from "src/components/common/customUiActions/CustomUiActionMenuItems.tsx";

export interface ShopOrdersDataGridProps {
	onlyShopOrderId: number | undefined;
	shopOrderSource?: ShopOrderSource;
	onSelectionChanged: (selectedShopOrders: ShopOrderView[]) => void;
}

export const ShopOrdersDataGrid = ({
	onlyShopOrderId,
	shopOrderSource,
	onSelectionChanged,
}: ShopOrdersDataGridProps) => {
	const showConfirmDialog = useConfirmDialog();
	const showInputDialog = useInputDialog();
	const { openDialog } = useGenericDialog();
	const { tenantConfig } = useTenantCustomizations();
	const hasPermission = useHasPermission();

	const { dataGridProps, onlySelectedRow, selectedRows, refreshData, currentParams } = useServerSideDataGridModel({
		fetchData: (params) => ShopOrderDataGridApi.getData(params),
		getRowId: (row) => row.shopOrderId,
		onSelectionChanged: onSelectionChanged,
		initialParams: {
			searchQuery: "",
			onlyDefaultSite: true,
			onlyShopOrderId: onlyShopOrderId,
			shopOrderSource: shopOrderSource,
			shopOrderStateFilter: genericNullableValue<ShopOrderState>(),
			includeCompleted: false,
			includeCancelled: false,
		},
		storedParams: ["onlyDefaultSite", "includeCompleted"],
		selectFirstRowOnLoad: onlyShopOrderId != null || shopOrderSource != null,
		gridId: "A8BE80CB37FC62C2",
	});

	const editingEnabled = hasPermission(Permissions.Production.ShopOrders.MANAGE);

	const multipleRowsSelected = selectedRows.length > 1;

	return (
		<ControlledAsyncFormCrudDataGrid<ShopOrderView>
			columns={[
				integerColumn({
					field: "shopOrderId",
					headerName: i18n.t("number_shortened"),
					width: 70,
				}),
				textColumn({ field: "siteName", headerName: i18n.t("site_short"), width: 70 }),
				enumColumn({
					field: "shopOrderState",
					headerName: i18n.t("state"),
					enumLabels: getShopOrderStateLabels(),
				}),
				textColumn({ field: "shopOrderBatchName", headerName: i18n.t("production_batch") }),
				textColumn({ field: "productionLineName", headerName: i18n.t("production_line") }),
				textColumn({ field: "sourceDescription", headerName: i18n.t("reference"), width: 200 }),
				textColumn({
					field: "configurationOrPartDescription",
					headerName: i18n.t("part"),
					width: 250,
					renderCell: ({ row }) => (
						<CellWithRemarkIndicator
							value={row.configurationOrPartDescription}
							remark={[
								{ label: i18n.t("sales_remark"), value: row.partSalesRemark },
								{ label: i18n.t("acquisition_remark"), value: row.partAcquisitionRemark },
							]}
						/>
					),
				}),
				floatColumn({ field: "quantity", headerName: i18n.t("quantity") }),
				textColumn({ field: "partUnit", headerName: i18n.t("unit") }),
				textColumn({ field: "packageInfo", headerName: i18n.t("packaging_info") }),
				textColumn({ field: "shopOrderNote", headerName: i18n.t("note") }),
				textColumn({
					field: "partRevision",
					headerName: i18n.t("revision"),
					valueGetter: (_, row) =>
						getPartRevisionShortDescription({
							revisionNumber: row.partRevision,
							note: row.partRevisionNote,
						}),
				}),
				floatColumn({ field: "capacityQuantity", headerName: i18n.t("capacity_quantity") }),
				dateColumn({ field: "demandDate", headerName: i18n.t("demand_date") }),
				dateTimeColumn({
					field: "plannedBeginDate",
					headerName: i18n.t("planned_begin_date"),
					width: 150,
				}),
				dateTimeColumn({
					field: "plannedEndDate",
					headerName: i18n.t("planned_end_date"),
					width: 150,
				}),
				dateTimeColumn({
					field: "creationDate",
					headerName: i18n.t("created_at"),
					width: 150,
				}),
				dateTimeColumn({
					field: "workInstructionPrinted",
					headerName: i18n.t("work_instruction_printed"),
					width: 150,
				}),
				...(tenantConfig.erp.shopOrderPartListPrintedEnabled ?
					[
						dateTimeColumn<ShopOrderView>({
							field: "partListPrinted",
							headerName: i18n.t("part_list_printed"),
							width: 150,
						}),
					]
				:	[]),
				dateTimeColumn({
					field: "releasedDate",
					headerName: i18n.t("released_at"),
					width: 150,
				}),
				dateTimeColumn({
					field: "actualBeginDate",
					headerName: i18n.t("actual_begin_date"),
					width: 150,
				}),
				dateTimeColumn({
					field: "actualEndDate",
					headerName: i18n.t("actual_end_date"),
					width: 150,
				}),
				textColumn({ field: "createdBy", headerName: i18n.t("created_by") }),
				enumColumn({
					field: "source",
					headerName: i18n.t("source"),
					enumLabels: getShopOrderSourceTypeLabels(),
					width: 150,
				}),
				floatColumn({
					field: "completedQuantity",
					headerName: i18n.t("completed_quantity"),
					width: 150,
				}),
				moneyColumn({ field: "workCost", headerName: i18n.t("work_cost") }),
				moneyColumn({ field: "materialCost", headerName: i18n.t("material_cost") }),
				moneyColumn({ field: "totalCost", headerName: i18n.t("total_cost") }),
				dateTimeColumn({ field: "cancelledDate", headerName: i18n.t("cancelled_at") }),
			]}
			actionBarComponents={
				<>
					<DocumentsOfObjectButton
						objectRef={nullableAavoObjectRef("SHOP_ORDER", onlySelectedRow?.shopOrderId)}
						editable={editingEnabled}
						disabled={multipleRowsSelected ? i18n.t("select_only_one_row") : undefined}
					/>
					<AavoTextField
						label={i18n.t("search")}
						onSubmit={async (value) => {
							await refreshData({ searchQuery: value });
						}}
					/>
					<AavoSelectField
						label={i18n.t("state")}
						options={ShopOrderStateValues}
						getOptionKey={(o) => o}
						getOptionLabel={(o) => getShopOrderStateLabel(o)}
						onChange={(value) => refreshData({ shopOrderStateFilter: value })}
					/>
					<AsyncButton
						label={i18n.t("snapshot")}
						icon={faTh}
						onClick={async () => {
							openDialog({
								title: i18n.t("snapshot"),
								size: "xl",
								agentContext: {
									key: defineAgentUiContextKey("erp.production.shopOrderSnapshot.shopOrdersDataGrid.dialog"),
									title: i18n.t("snapshot"),
									pathSegments: [`${i18n.t("shop_order")} ${onlySelectedRow!.shopOrderId}`],
									data: {
										shopOrderId: onlySelectedRow!.shopOrderId,
									},
								},
								content: <ShopOrderStateView shopOrderId={onlySelectedRow!.shopOrderId} />,
							});
						}}
						variant={"outlined"}
						disabled={
							multipleRowsSelected ? i18n.t("select_only_one_row")
							: onlySelectedRow == null ?
								true
							:	false
						}
					/>
				</>
			}
			actionBarMenuComponents={[
				<CustomUiActionObjectMenuItems key={"customFunctionButtons"} view={"SHOP_ORDERS"} />,
				<MenuCheckbox
					key={"showCompleted"}
					label={i18n.t("show_completed")}
					checked={currentParams.includeCompleted}
					onChange={(value) => refreshData({ includeCompleted: value })}
				/>,
				<MenuCheckbox
					key={"showCancelled"}
					label={i18n.t("show_cancelled")}
					checked={currentParams.includeCancelled}
					onChange={(value) => refreshData({ includeCancelled: value })}
				/>,
				<MenuCheckbox
					key={"onlyDefaultSite"}
					label={i18n.t("only_default_site")}
					checked={currentParams.onlyDefaultSite}
					onChange={async (value) => {
						await refreshData({ onlyDefaultSite: value });
					}}
				/>,
			]}
			rowContextMenuComponents={({ row, onlySingleRowSelected, allSelectedRows }) => [
				<CustomUiActionObjectMenuItems
					key={"customFunctionButtons"}
					view={"SHOP_ORDERS"}
					objectIds={allSelectedRows.map((order) => order.shopOrderId)}
				/>,
				onlySingleRowSelected && editingEnabled && (
					<AsyncMenuButton
						key={"release"}
						label={i18n.t("release")}
						icon={faShare}
						onClick={async () => {
							await releaseShopOrder({
								shopOrderId: row.shopOrderId,
								showConfirmDialog: showConfirmDialog,
							});
							await refreshData();
						}}
						hidden={row.shopOrderState !== "PLANNED"}
					/>
				),
				onlySingleRowSelected && editingEnabled && (
					<AsyncMenuButton
						key={"revertRelease"}
						label={i18n.t("revert_release")}
						icon={faUndo}
						onClick={async () => {
							const confirmed = await showConfirmDialog({
								title: i18n.t("revert_release"),
								message: i18n.t("confirm_revert_release_shop_order"),
							});
							if (!confirmed) return;

							await ShopOrderActionApi.revertReleaseShopOrder({
								shopOrderId: row.shopOrderId,
							});
							await refreshData();
						}}
						hidden={row.shopOrderState !== "RELEASED"}
					/>
				),
				onlySingleRowSelected && editingEnabled && (
					<AsyncMenuButton
						key={"close"}
						label={i18n.t("close")}
						icon={faCheck}
						onClick={async () => {
							await closeShopOrder({
								shopOrderId: row.shopOrderId,
								showConfirmDialog: showConfirmDialog,
							});
							await refreshData();
						}}
						hidden={row.shopOrderState !== "STARTED"}
					/>
				),
				onlySingleRowSelected && editingEnabled && (
					<AsyncMenuButton
						key={"revertClose"}
						label={i18n.t("revert_close")}
						icon={faUndo}
						onClick={async () => {
							const confirmed = await showConfirmDialog({
								title: i18n.t("revert_close"),
								message: i18n.t("confirm_revert_close_shop_order"),
							});
							if (!confirmed) return;

							await ShopOrderActionApi.revertCloseShopOrder({
								shopOrderId: row.shopOrderId,
							});
							await refreshData();
						}}
						hidden={row.shopOrderState !== "READY"}
					/>
				),
				onlySingleRowSelected && editingEnabled && (
					<AsyncMenuButton
						key={"reportQuantity"}
						label={i18n.t("report_quantity")}
						icon={faStopwatch20}
						onClick={async () => {
							openFormOnDialog({
								openDialog,
								title: i18n.t("increase_completed_quantity"),
								size: "sm",
								confirmCloseIfEdited: false,
								component: IncreaseShopOrderCompletedQuantityForm,
								agentContext: {
									key: defineAgentUiContextKey("erp.production.increaseShopOrderCompletedQuantity.formDialog"),
									title: i18n.t("increase_completed_quantity"),
									pathSegments: [`${i18n.t("shop_order")} ${row.shopOrderId}`],
									data: { shopOrderId: row.shopOrderId },
								},
								props: { shopOrderId: row.shopOrderId },
								onSubmit: refreshData,
							});
						}}
						hidden={row.shopOrderState !== "STARTED"}
					/>
				),
				onlySingleRowSelected &&
					editingEnabled &&
					(["RELEASED", "STARTED", "READY"] as ShopOrderState[]).includes(row.shopOrderState) &&
					row.completedQuantity < row.quantity && (
						<AsyncMenuButton
							key={"createSupplementShopOrder"}
							label={i18n.t("create_supplement_shop_order")}
							icon={faPlusHexagon}
							onClick={async () => {
								const note = await showInputDialog({
									title: i18n.t("create_supplement_shop_order"),
									fieldLabel: i18n.t("note"),
									type: "string",
									defaultValue: "",
									required: true,
								});
								if (note == null) return;
								await ShopOrderActionApi.createSupplementShopOrder({
									shopOrderId: row.shopOrderId,
									note,
								});
								await refreshData();
							}}
						/>
					),
				editingEnabled && (
					<AsyncMenuButton
						key={"cancel"}
						label={i18n.t("cancel")}
						icon={faBan}
						onClick={async () => {
							await cancelShopOrders({
								shopOrderIds: allSelectedRows.map((r) => r.shopOrderId),
								showConfirmDialog: showConfirmDialog,
							});
							await refreshData();
						}}
						disabled={allSelectedRows.some(
							(r) => r.shopOrderState === "CANCELLED" || r.shopOrderState === "READY",
						)}
					/>
				),
				<Divider key={"divider1"}></Divider>,
				onlySingleRowSelected && (
					<AsyncMenuButton
						key={"printWorkInstruction"}
						label={i18n.t("work_instruction")}
						icon={faPrint}
						onClick={async () => {
							const fileHandle = await ShopOrderPrintApi.printShopOrderWorkInstructions({
								shopOrderIds: [row.shopOrderId],
							});
							downloadFile(fileHandle);
						}}
					/>
				),
				<AsyncMenuButton
					key={"printLabel"}
					label={i18n.t("sticker")}
					icon={faPrint}
					onClick={async () => {
						const fileHandle = await ShopOrderPrintApi.printShopOrderLabel({
							shopOrderIds: allSelectedRows.map((r) => r.shopOrderId),
							quantityType: "FULL_QUANTITY",
						});
						downloadFile(fileHandle);
					}}
				/>,
				<Divider key={"divider2"}></Divider>,
				onlySingleRowSelected &&
					tenantConfig.erp.surveysEnabled && [
						<AsyncMenuButton
							key={"showSurveys"}
							label={i18n.t("show_surveys")}
							icon={faStickyNote}
							onClick={async () => {
								const operations = await ShopOrderOperationApi.getShopOrderOperations({
									shopOrderId: row.shopOrderId,
								});
								openDialog({
									title: i18n.t("surveys"),
									size: "fullscreen",
									agentContext: {
										key: defineAgentUiContextKey("erp.production.shopOrderSurveys.dialog"),
										title: i18n.t("surveys"),
										pathSegments: [`${i18n.t("shop_order")} ${row.shopOrderId}`],
										data: {
											shopOrderId: row.shopOrderId,
											shopOrderOperationIds: operations.map((o) => o.shopOrderOperationId),
										},
									},
									content: (
										<SurveySubmissionsOfObjectsView
											objectType={"SHOP_ORDER_OPERATION"}
											objectIds={operations.map((o) => o.shopOrderOperationId)}
										/>
									),
								});
							}}
						/>,
						<AsyncMenuButton
							key={"printSurveys"}
							label={i18n.t("print_all_surveys")}
							icon={faFileDownload}
							onClick={async () => {
								const fileHandle = await ShopOrderPrintApi.printSurveysOfShopOrderOperations({
									shopOrderId: row.shopOrderId,
								});
								downloadFile(fileHandle);
							}}
						/>,
						<Divider key={"divider3"}></Divider>,
					],
				onlySingleRowSelected && row.shopOrderBatchId != null && (
					<AsyncMenuButton
						key={"showProductionBatch"}
						label={i18n.t("show_shop_order_batch")}
						icon={faCubes}
						onClick={async () => {
							openDialog({
								title: i18n.t("shop_order_batch"),
								agentContext: {
									key: defineAgentUiContextKey("erp.production.shopOrderBatchFromShopOrder.dialog"),
									title: i18n.t("shop_order_batch"),
									pathSegments: [`${i18n.t("shop_order")} ${row.shopOrderId}`],
									data: {
										shopOrderId: row.shopOrderId,
										shopOrderBatchId: row.shopOrderBatchId ?? null,
									},
								},
								content: <ShopOrderBatchesView onlyShopOrderBatchId={row.shopOrderBatchId!} />,
							});
						}}
					/>
				),
				onlySingleRowSelected &&
					row.partIsConfigurable && [
						editingEnabled &&
							row.partIsConfigurable &&
							(row.shopOrderState === "INITIAL" || row.shopOrderState === "PLANNED") && (
								<AsyncMenuButton
									key={"configure"}
									label={i18n.t("configure")}
									icon={faCog}
									onClick={async () => {
										const confirmed = await showConfirmDialog({
											title: i18n.t("configure"),
											message: i18n.t("confirm_reconfigure_shop_order"),
										});
										if (!confirmed) return;
										openDialog(({ closeDialog, onDataDirtyStateChanged }) => ({
											title: i18n.t("configuration"),
											size: "fullscreen",
											agentContext: {
												key: defineAgentUiContextKey("erp.production.shopOrderConfiguration.dialog"),
												title: i18n.t("configuration"),
												pathSegments: [`${i18n.t("shop_order")} ${row.shopOrderId}`],
												data: {
													shopOrderId: row.shopOrderId,
													partConfigurationId: row.partConfigurationId ?? null,
												},
											},
											content: (
												<ShopOrderConfiguratorView
													configuratorType={{
														type: "shopOrder",
														shopOrderId: row.shopOrderId,
														explicitPartConfigurationId: null,
													}}
													closeDialog={closeDialog}
													refreshSourceView={refreshData}
													onDataDirtyStateChanged={onDataDirtyStateChanged}
												/>
											),
										}));
									}}
								/>
							),
						row.partConfigurationId != null && (
							<OpenPartConfigurationButton
								key={"configuration"}
								partConfigurationId={row.partConfigurationId}
								partConfiguratorType={{
									type: "shopOrder",
									shopOrderId: row.shopOrderId,
									explicitPartConfigurationId: null,
								}}
							/>
						),
						row.partConfigurationId != null && (
							<OpenConfigurationHistoryButton
								key={"openConfigurationHistoryButton"}
								objectType={"SHOP_ORDER"}
								objectId={row.shopOrderId}
								refreshSourceView={refreshData}
								restoreConfirmationMessage={i18n.t("confirm_reconfigure_shop_order")}
							/>
						),
						<Divider key={"divider4"} />,
					],
				onlySingleRowSelected && (
					<OpenCustomerOrderInfoButton key={"customerOrderInfo"} customerOrderId={row.customerOrderId} />
				),
				onlySingleRowSelected && <OpenShopOrderSourceButton key={"openSource"} row={row} />,
				onlySingleRowSelected && <OpenSitePartButton partId={row.partId} key={"part"} />,
				onlySingleRowSelected && (
					<AsyncMenuButton
						key={"costEvents"}
						label={i18n.t("cost_events")}
						icon={faHourglassHalf}
						onClick={() => {
							openDialog({
								title: `${i18n.t("cost_events")} | ${row.partLongName}`,
								agentContext: {
									key: defineAgentUiContextKey("erp.production.shopOrderCostEvents.dialog"),
									title: i18n.t("cost_events"),
									pathSegments: [`${i18n.t("shop_order")} ${row.shopOrderId}`],
									data: { shopOrderId: row.shopOrderId },
								},
								content: <ShopOrderCostEventsDataGrid shopOrderId={row.shopOrderId} />,
							});
						}}
					/>
				),
				onlySingleRowSelected && (
					<OpenObjectChangeLogButton
						key={"changeLog"}
						objectRef={{
							objectType: "SHOP_ORDER",
							objectId: row.shopOrderId,
						}}
					/>
				),
			]}
			form={{
				addRowEnabled: editingEnabled,
				editEnabled: editingEnabled,
				dialogSize: "md",
				dialogTitle: i18n.t("shop_order"),
				agentContext: (row) => ({
					key: defineAgentUiContextKey("erp.production.shopOrder.shopOrdersDataGrid.formDialog"),
					title: i18n.t("shop_order"),
					data: {
						shopOrderId: row?.shopOrderId ?? null,
						partId: row?.partId ?? null,
						sourceType: row?.source ?? null,
						sourceId: row?.sourceId ?? null,
					},
				}),
				component: ({ row, ...other }) => <ShopOrderForm shopOrderId={row?.shopOrderId ?? null} {...other} />,
			}}
			agentContext={{
				key: defineAgentUiContextKey("erp.production.shopOrders.grid"),
				data: {
					onlyShopOrderId: onlyShopOrderId ?? null,
					sourceType: shopOrderSource?.sourceType ?? null,
					sourceId: shopOrderSource?.sourceId ?? null,
					shopOrderStateFilter: currentParams.shopOrderStateFilter ?? null,
				},
				rowFields: ["shopOrderId", "partId", "source", "sourceId", "shopOrderBatchId"],
				selectedRowPathSegment: (row) => `${i18n.t("shop_order")} ${row.shopOrderId}`,
			}}
			{...dataGridProps}
		/>
	);
};
